import moment from 'moment';
import t from '../../../util/translation/translation';

// ISO weekdays: 1 = Monday ... 7 = Sunday
export const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

export const BOOKING_MODES = ['occurrence', 'series', 'both'];

const capitalise = (text) => text.charAt(0).toUpperCase() + text.slice(1);

// Short weekday name in the admin's language, capitalised ("Tue", "Tis")
export const weekdayName = (isoWeekday) =>
  capitalise(moment().isoWeekday(isoWeekday).format('ddd'));

// A day ('YYYY-MM-DD' or ISO) as "Tue 4 Nov 2026"
export const formatDay = (date) =>
  capitalise(moment(date).format('ddd D MMM YYYY'));

// A start as "Tue 4 Nov 2026, 18:00"
export const formatDayTime = (date) =>
  capitalise(moment(date).format('ddd D MMM YYYY, HH:mm'));

const toMinutes = (time) => {
  const [hours, minutes] = (time || '0:0').split(':').map(Number);
  return hours * 60 + minutes;
};

const toTime = (minutes) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(
    minutes % 60
  ).padStart(2, '0')}`;

// The start times on each scheduled day, 'HH:mm', earliest first
export const dailyTimes = (schedule) => {
  if (!schedule) return [];
  if (schedule.kind === 'interval') {
    const step = Number(schedule.everyMinutes);
    if (!schedule.from || !schedule.to || !(step >= 5)) return [];
    const times = [];
    // "to" is the last start, not the last end
    for (
      let minutes = toMinutes(schedule.from);
      minutes <= toMinutes(schedule.to) && times.length < 300;
      minutes += step
    ) {
      times.push(toTime(minutes));
    }
    return times;
  }
  return [...new Set((schedule.times || []).filter(Boolean))].sort();
};

export const daysText = (weekdays = []) => {
  const sorted = ISO_WEEKDAYS.filter((day) => weekdays.includes(day));
  if (sorted.length === 7) return t('EventSeries.everyDay');
  const names = sorted.map(weekdayName);
  return names.length === 2 ? names.join(' & ') : names.join(', ');
};

// "Tue & Thu 18:00" / "Sat & Sun, every 30 min 11:00–15:00"
export const scheduleSummary = (schedule) => {
  if (!schedule) return '';
  const days = daysText(schedule.weekdays);
  if (schedule.kind === 'interval') {
    return t('EventSeries.intervalSummary', {
      days,
      minutes: schedule.everyMinutes,
      from: schedule.from,
      to: schedule.to,
    });
  }
  return `${days} ${dailyTimes(schedule).join(', ')}`.trim();
};

// The occurrences the service will have created: from the first date (or
// today) up to weeksAhead weeks from now and the last date, skipping the
// skip dates. Computed here only to preview them; starts as moments.
export const occurrenceStarts = (
  { schedule, skipDates = [], weeksAhead = 8 },
  limit = 500
) => {
  if (!schedule?.startDate || !schedule.weekdays?.length) return [];
  const times = dailyTimes(schedule);
  const firstDay = moment(schedule.startDate, 'YYYY-MM-DD');
  if (!times.length || !firstDay.isValid()) return [];
  const now = moment();
  const windowEnd = now
    .clone()
    .startOf('day')
    .add(Number(weeksAhead) * 7, 'days');
  const lastDay = schedule.endDate
    ? moment.min(moment(schedule.endDate, 'YYYY-MM-DD'), windowEnd)
    : windowEnd;
  const starts = [];
  const day = moment.max(firstDay, now.clone().startOf('day')).clone();
  while (day.isSameOrBefore(lastDay, 'day') && starts.length < limit) {
    if (
      schedule.weekdays.includes(day.isoWeekday()) &&
      !skipDates.includes(day.format('YYYY-MM-DD'))
    ) {
      times.forEach((time) => {
        const [hours, minutes] = time.split(':').map(Number);
        const start = day.clone().hours(hours).minutes(minutes).seconds(0);
        if (start.isAfter(now)) starts.push(start);
      });
    }
    day.add(1, 'day');
  }
  return starts;
};

const isMissingTranslation = (text) =>
  typeof text !== 'string' || text.startsWith('Missing translation');

// The events service answers 400s with a translation key as the message
// (series.HasBookings, event.Cancelled, ...); those are under "api". Plain
// string errors (already translated by Api.axios) are shown as they are.
export const getApiErrorMessage = (
  err,
  fallbackKey = 'shared.somethingWentWrong'
) => {
  const code = err?.data?.message;
  if (typeof code === 'string') {
    try {
      const text = t(`api.${code}`);
      if (!isMissingTranslation(text)) return text;
    } catch {
      // Not a known code - fall through
    }
  }
  if (typeof err === 'string') return err;
  return fallbackKey ? t(fallbackKey) : null;
};
